// services/consentService.js
import {
  API_CONSENT_CREATE,
  API_CONSENT_DETAILS,
  API_CONSENT_REVOKE,
  API_CONSENT_CHECK_PERMISSION,
  API_CONSENT_BY_PATIENT,
  API_CONSENT_BY_GRANTED_TO,
  API_CONSENT_AVAILABLE_ACTIVITIES,
} from "../../../../helpers/urlHelper";

/**
 * 🔐 Servicio de Gestión de Consentimientos
 * Maneja todas las operaciones relacionadas con consentimientos GDPR
 */
class ConsentService {
  
  // ========================================================================
  // 🔧 MÉTODOS PRIVADOS - CONSTRUCCIÓN DE URLs
  // ========================================================================

  /**
   * Construir URL para obtener consentimientos de un paciente con filtros
   * @param {string} patientId - ID del paciente
   * @param {Object} filters - Filtros opcionales
   * @param {string} [filters.status] - Estado: 'active', 'revoked'
   * @param {string} [filters.grantedToType] - Tipo: 'doctor', 'center'
   * @param {boolean} [filters.includeExpired] - Incluir expirados
   * @returns {string} URL completa con query params
   */
  _buildPatientConsentsUrl(patientId, filters = {}) {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.grantedToType) queryParams.append('grantedToType', filters.grantedToType);
    if (filters.includeExpired !== undefined) queryParams.append('includeExpired', filters.includeExpired);
    
    const queryString = queryParams.toString();
    return `${API_CONSENT_BY_PATIENT}/${patientId}${queryString ? `?${queryString}` : ''}`;
  }

  /**
   * Construir URL para obtener consentimientos otorgados a un usuario
   * @param {string} userId - ID del doctor o centro
   * @param {Object} filters - Filtros opcionales
   * @param {string} [filters.status] - Estado del consentimiento
   * @param {boolean} [filters.onlyValid] - Solo consentimientos válidos
   * @returns {string} URL completa con query params
   */
  _buildGrantedConsentsUrl(userId, filters = {}) {
    const queryParams = new URLSearchParams();
    if (filters.status) queryParams.append('status', filters.status);
    if (filters.onlyValid !== undefined) queryParams.append('onlyValid', filters.onlyValid);
    
    const queryString = queryParams.toString();
    return `${API_CONSENT_BY_GRANTED_TO}/${userId}${queryString ? `?${queryString}` : ''}`;
  }

  /**
   * Construir URL para obtener detalle de un consentimiento
   * @param {string} consentId - ID del consentimiento
   * @returns {string} URL completa
   */
  _buildConsentDetailUrl(consentId) {
    return `${API_CONSENT_DETAILS}/${consentId}`;
  }

  /**
   * Construir URL para revocar un consentimiento
   * @param {string} consentId - ID del consentimiento
   * @returns {string} URL completa
   */
  _buildConsentRevokeUrl(consentId) {
    return `${API_CONSENT_REVOKE}/${consentId}/revoke`;
  }

  /**
   * Construir URL para obtener actividades disponibles
   * @param {string} patientId - ID del paciente
   * @param {Object} filters - Filtros requeridos
   * @param {string} filters.grantedToId - ID del doctor o centro
   * @param {string} filters.grantedToType - Tipo: 'doctor' o 'center'
   * @returns {string} URL completa con query params
   */
  _buildAvailableActivitiesUrl(patientId, filters = {}) {
    const queryParams = new URLSearchParams();
    if (filters.grantedToId) queryParams.append('grantedToId', filters.grantedToId);
    if (filters.grantedToType) queryParams.append('grantedToType', filters.grantedToType);
    
    const queryString = queryParams.toString();
    return `${API_CONSENT_AVAILABLE_ACTIVITIES}/${patientId}${queryString ? `?${queryString}` : ''}`;
  }

  // ========================================================================
  // 📝 MÉTODOS PÚBLICOS - OPERACIONES CRUD
  // ========================================================================

  /**
   * Crear un nuevo consentimiento
   * @param {Object} consentData - Datos del consentimiento
   * @param {string} consentData.patientId - ID del paciente
   * @param {string} consentData.grantedToType - Tipo: 'doctor' o 'center'
   * @param {string} consentData.grantedToId - ID del destinatario
   * @param {Array} consentData.moduleConsents - Módulos consentidos
   * @param {string} consentData.durationType - Tipo: 'permanent' o 'temporary'
   * @param {Date} [consentData.expiresOn] - Fecha de expiración (si es temporal)
   * @param {string} consentData.purpose - Propósito del consentimiento
   * @param {string} [consentData.notes] - Notas adicionales
   * @param {string} [consentData.verificationMethod] - Método de verificación
   * @returns {Promise<Object>} Respuesta con el consentimiento creado
   */
  async createConsent(consentData) {
    try {
      const response = await fetch(API_CONSENT_CREATE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(consentData)
      });
      return await response.json();
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Obtener todos los consentimientos de un paciente
   * @param {string} patientId - ID del paciente
   * @param {Object} filters - Filtros opcionales
   * @returns {Promise<Object>} Lista de consentimientos con resumen
   */
  async getPatientConsents(patientId, filters = {}) {
  try {
    const url = this._buildPatientConsentsUrl(patientId, filters);
    console.log('🔍 URL completa:', url); // ⬅️ AGREGAR ESTO
    const response = await fetch(url);
    console.log('📡 Response status:', response.status); // ⬅️ Y ESTO
    console.log('📡 Response headers:', response.headers.get('content-type')); // ⬅️ Y ESTO
    
    const text = await response.text(); // ⬅️ CAMBIAR A TEXT PRIMERO
    console.log('📄 Response body:', text.substring(0, 200)); // ⬅️ VER QUÉ LLEGA
    
    return JSON.parse(text); // ⬅️ INTENTAR PARSEAR
  } catch (error) {
    throw this._handleError(error);
  }
}

  /**
   * Obtener consentimientos otorgados a un doctor/centro
   * @param {string} userId - ID del doctor o centro
   * @param {Object} filters - Filtros opcionales
   * @returns {Promise<Object>} Lista de consentimientos
   */
  async getConsentsGrantedTo(userId, filters = {}) {
    try {
      const url = this._buildGrantedConsentsUrl(userId, filters);
      const response = await fetch(url);
      return await response.json();
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Obtener detalle de un consentimiento específico
   * @param {string} consentId - ID del consentimiento
   * @returns {Promise<Object>} Detalle completo del consentimiento
   */
  async getConsentDetail(consentId) {
    try {
      const url = this._buildConsentDetailUrl(consentId);
      const response = await fetch(url);
      return await response.json();
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Revocar un consentimiento
   * @param {string} consentId - ID del consentimiento
   * @param {Object} revokeData - Datos de revocación
   * @param {string} [revokeData.reason] - Razón de la revocación
   * @param {string} [revokeData.revokedBy] - ID del usuario que revoca
   * @returns {Promise<Object>} Consentimiento revocado
   */
  async revokeConsent(consentId, revokeData = {}) {
    try {
      const url = this._buildConsentRevokeUrl(consentId);
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(revokeData)
      });
      return await response.json();
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Verificar si existe permiso de acceso
   * @param {Object} permissionData - Datos para verificación
   * @param {string} permissionData.patientId - ID del paciente
   * @param {string} permissionData.requestorId - ID del solicitante
   * @param {string} permissionData.requestorType - Tipo: 'doctor' o 'center'
   * @param {string} permissionData.moduleName - Nombre del módulo
   * @param {string} [permissionData.elementId] - ID del elemento específico
   * @returns {Promise<Object>} { granted: boolean, consent: Object }
   */
  async checkPermission(permissionData) {
    try {
      const response = await fetch(API_CONSENT_CHECK_PERMISSION, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(permissionData)
      });
      return await response.json();
    } catch (error) {
      throw this._handleError(error);
    }
  }

  /**
   * Obtener actividades disponibles para consentir
   * @param {string} patientId - ID del paciente
   * @param {Object} filters - Filtros requeridos
   * @param {string} filters.grantedToId - ID del doctor o centro
   * @param {string} filters.grantedToType - Tipo: 'doctor' o 'center'
   * @returns {Promise<Object>} Actividades disponibles y ya consentidas
   */
  async getAvailableActivities(patientId, filters = {}) {
    try {
      const url = this._buildAvailableActivitiesUrl(patientId, filters);
      const response = await fetch(url);
      return await response.json();
    } catch (error) {
      throw this._handleError(error);
    }
  }

  // ========================================================================
  // 🚀 MÉTODOS DE CONVENIENCIA
  // ========================================================================

  /**
   * Obtener solo consentimientos activos de un paciente
   * @param {string} patientId - ID del paciente
   * @returns {Promise<Object>} Consentimientos activos
   */
  async getActiveConsents(patientId) {
    return this.getPatientConsents(patientId, { 
      status: 'active', 
      includeExpired: false 
    });
  }

  /**
   * Obtener consentimientos revocados de un paciente
   * @param {string} patientId - ID del paciente
   * @returns {Promise<Object>} Consentimientos revocados
   */
  async getRevokedConsents(patientId) {
    return this.getPatientConsents(patientId, { status: 'revoked' });
  }

  /**
   * Obtener solo consentimientos válidos (activos y no expirados)
   * @param {string} patientId - ID del paciente
   * @returns {Promise<Object>} Consentimientos válidos
   */
  async getValidConsents(patientId) {
    return this.getPatientConsents(patientId, { 
      status: 'active',
      includeExpired: false 
    });
  }

  /**
   * Verificar si un doctor tiene acceso a un módulo específico
   * @param {string} patientId - ID del paciente
   * @param {string} doctorId - ID del doctor
   * @param {string} moduleName - Nombre del módulo
   * @returns {Promise<boolean>} true si tiene acceso
   */
  async hasDoctorAccessToModule(patientId, doctorId, moduleName) {
    try {
      const result = await this.checkPermission({
        patientId,
        requestorId: doctorId,
        requestorType: 'doctor',
        moduleName
      });
      return result.data?.granted || false;
    } catch (error) {
      console.error('Error checking doctor access:', error);
      return false;
    }
  }

  /**
   * Crear un consentimiento permanente completo (todos los módulos)
   * @param {string} patientId - ID del paciente
   * @param {string} grantedToType - Tipo: 'doctor' o 'center'
   * @param {string} grantedToId - ID del destinatario
   * @param {string} purpose - Propósito del consentimiento
   * @returns {Promise<Object>} Consentimiento creado
   */
  async createFullPermanentConsent(patientId, grantedToType, grantedToId, purpose) {
    return this.createConsent({
      patientId,
      grantedToType,
      grantedToId,
      moduleConsents: [{
        module: 'all',
        fullConsent: true,
        allowedActions: ['view', 'download', 'share']
      }],
      durationType: 'permanent',
      purpose,
      verificationMethod: 'password'
    });
  }

  /**
   * Crear un consentimiento temporal para módulos específicos
   * @param {string} patientId - ID del paciente
   * @param {string} grantedToType - Tipo: 'doctor' o 'center'
   * @param {string} grantedToId - ID del destinatario
   * @param {Array<string>} modules - Lista de módulos
   * @param {Date|string} expiresOn - Fecha de expiración
   * @param {string} purpose - Propósito del consentimiento
   * @returns {Promise<Object>} Consentimiento creado
   */
  async createTemporaryConsent(patientId, grantedToType, grantedToId, modules, expiresOn, purpose) {
    const moduleConsents = modules.map(module => ({
      module,
      fullConsent: true,
      allowedActions: ['view']
    }));
    
    return this.createConsent({
      patientId,
      grantedToType,
      grantedToId,
      moduleConsents,
      durationType: 'temporary',
      expiresOn,
      purpose,
      verificationMethod: 'password'
    });
  }

  /**
   * Crear consentimiento granular para actividades específicas
   * @param {string} patientId - ID del paciente
   * @param {string} grantedToType - Tipo: 'doctor' o 'center'
   * @param {string} grantedToId - ID del destinatario
   * @param {Array<Object>} moduleConsents - Array de módulos con elementos específicos
   * @param {string} purpose - Propósito del consentimiento
   * @param {Object} options - Opciones adicionales
   * @returns {Promise<Object>} Consentimiento creado
   */
  async createGranularConsent(patientId, grantedToType, grantedToId, moduleConsents, purpose, options = {}) {
    return this.createConsent({
      patientId,
      grantedToType,
      grantedToId,
      moduleConsents,
      durationType: options.durationType || 'permanent',
      expiresOn: options.expiresOn,
      purpose,
      notes: options.notes,
      verificationMethod: options.verificationMethod || 'password'
    });
  }

  // ========================================================================
  // 🛠️ UTILIDADES
  // ========================================================================

  /**
   * Manejo de errores del servicio
   * @private
   */
  _handleError(error) {
    if (error.response) {
      return {
        code: error.response.data?.error?.code || 'SERVER_ERROR',
        message: error.response.data?.error?.message || 'Error en el servidor',
        details: error.response.data?.error?.details,
        status: error.response.status
      };
    } else if (error.request) {
      return {
        code: 'NETWORK_ERROR',
        message: 'Error de conexión con el servidor'
      };
    } else {
      return {
        code: 'UNKNOWN_ERROR',
        message: error.message || 'Error desconocido'
      };
    }
  }
}

export default new ConsentService();